import { FC, ReactNode } from 'react';
import { Avatar, Col, Divider, Row } from 'antd';
import {
    TbBuildingBank,
    TbBuildingCommunity,
    TbBuildingEstate,
    TbBuildingFactory2,
    TbChartArcs3,
    TbLayoutGridAdd,
    TbReportAnalytics,
    TbSchool,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { LearnMoreFooter } from '@/shared';
import SelectableOptionCard from '@/shared/components/cards/SelectableOptionCard';
import { WORKSPACE_TEMPLATE_OPTIONS, WorkspaceTemplateKey, WorkspaceTemplateOption } from '@/types/workspaceTemplates';

interface WorkspaceTemplateSelectorProps {
    bordered?: boolean;
    disabled?: boolean;
    onChange: (templateKey: WorkspaceTemplateKey) => void;
    showLearnMoreFooter?: boolean;
    value: WorkspaceTemplateKey;
}

const templateIcons: Record<WorkspaceTemplateKey, ReactNode> = {
    newWorkspace: <TbLayoutGridAdd size='1rem' />,
    assetManagerRia: <TbBuildingBank size='1rem' />,
    hedgeFund: <TbChartArcs3 size='1rem' />,
    familyOffice: <TbBuildingEstate size='1rem' />,
    endowment: <TbBuildingCommunity size='1rem' />,
    sponsoredResearch: <TbReportAnalytics size='1rem' />,
    corporateDevelopment: <TbBuildingFactory2 size='1rem' />,
    higherEducation: <TbSchool size='1rem' />,
};

const WorkspaceTemplateSelector: FC<WorkspaceTemplateSelectorProps> = ({
    bordered = false,
    disabled = false,
    onChange,
    showLearnMoreFooter = true,
    value,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    const renderIcon = (option: WorkspaceTemplateOption, index: number) => {
        const isDefault = option.key === 'newWorkspace';

        return (
            <Avatar
                aria-hidden='true'
                icon={templateIcons[option.key]}
                shape='square'
                size='small'
                style={{
                    backgroundColor: isDefault ? token.colorBgContainer : token.gold1,
                    border: `1px solid ${isDefault ? token.colorBorder : token.gold2}`,
                    color: isDefault ? token.colorTextTertiary : index > 3 ? token.gold6 : token.gold5,
                }}
            />
        );
    };

    return (
        <>
            <div
                style={
                    bordered
                        ? {
                              border: `1px dashed ${token.colorBorder}`,
                              borderRadius: token.borderRadius,
                              padding: '0.75rem',
                          }
                        : undefined
                }
            >
                <Row aria-label='Workspace templates' gutter={[12, 12]} role='radiogroup'>
                    {WORKSPACE_TEMPLATE_OPTIONS.map((option, index) => (
                        <Col key={option.key} xs={24} sm={12}>
                            <SelectableOptionCard
                                borderStyle={option.key === 'newWorkspace' ? 'solid' : 'dashed'}
                                description={option.description}
                                disabled={disabled}
                                icon={renderIcon(option, index)}
                                label={option.label}
                                onSelect={() => onChange(option.key)}
                                selected={value === option.key}
                            />
                        </Col>
                    ))}
                </Row>
            </div>
            {showLearnMoreFooter ? (
                <>
                    <Divider style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }} />
                    <LearnMoreFooter topic='Workspace Templates' />
                </>
            ) : null}
        </>
    );
};

export default WorkspaceTemplateSelector;
